// THE COMPILE QUEUE (R8b "one compile queue"): every shader program the
// client builds off the frame, and the big texture uploads before entry, go
// through ONE queue with ONE scheduler — the first view behind the beam-in
// overlay, the water variants, the GLB / ways roots about to join, the
// pre-entry walk at a card / the Beam, the warm-up behind the overlay, the
// late pass and the optic copies. main.js keeps the POLICIES (what to ask,
// when, at which priority: preWarm, fvTick, warmWater, warmShaders, warmLate,
// the drip feeds' prepare); this file is the MECHANICS. No
// three import: node runs it over a fake renderer (scripts/check-compileq.mjs).
//
//   const q = createCompileQueue({ renderer, camera, act, now, setTimeout, frame, taskRoom });
//       frame() -> { compile_ms, upload_kb }: THE FRAME BUDGET's share of the
//          queue (main.js: sched.js frame, renderconfig device_budgets.<class>
//          .frame; the gates: FRAME_GATE)
//   q.want(root, { label, use, target, pass, view, prio, stop })
//       -> Promise of the programs it made, once each is compiled, linked and
//          first-used (or gone). label: the act label of its compiles (the
//          load report's lt_by), use: of its first uses (label.firstUse);
//          target: the scene whose lights decide the programs (root itself
//          when null); pass: an optic pass { cam, rt } (weapons.opticPasses);
//          view: { x, z, only } the first view at (x, z) first (`only`:
//          nothing else); prio: a PRIO name or a number; stop(): true ends
//          its compiles (what it made is still first-used).
//   q.upload(root, { label, prio, all, stop }) -> the big pending textures of
//          root's visible objects (every object: `all`), under the frame's
//          upload bytes (UPLOAD BUDGET below).
//   q.steps(pieces, { label, prio, stop }) -> a texture job of a caller's own
//          upload PIECES [{ bytes, run }] (R8d6b: groundmat's window row bands,
//          the material array's layers), run in order under the same UPLOAD
//          BUDGET — a slow piece (a write stalled behind the queued frame)
//          spends the task's compile_ms, the next one goes in the next task;
//          resolves once every piece ran (or stop()). One fresh array a call
//          (a re-ask of the same array while it runs would replay it).
//   q.pin(obj)   the registry of what is held for good (terrain's variant
//                keepers: a variant's program outlives its last node).
//   q.busy(cls)  a job of that class queued or running (any: no argument).
//   q.uncompiled(o, untried), q.left(root, x, z): the scans the landing and
//                the first view ask.
//   q.drawn(fn, label): runs a draw and counts the programs three built
//                INSIDE it (state().inFrame: the acceptance number).
//   q.state()    per class queued / running / done; in flight; made /
//                first-used / gone; uploads; pinned; inFrame.
//
// A re-want of the same root (+ pass, + label) while it is queued takes the
// newer asks; while it runs, ONE re-scan after it with the latest asks (N
// re-wants: one re-scan, not N). A stopped job (a newer target, the overlay
// gone) ends its compiles at the next task.
//
// NON-BLOCKING LINK (KHR_parallel_shader_compile). three (r170) issues
// compileShader + linkProgram inside compile() and returns; with the
// extension the driver links off this thread and program.isReady() asks
// COMPLETION_STATUS, which never waits for the link. getUniforms (three's
// first use: LINK_STATUS, the info logs, the location queries) DOES wait, so
// it is only ever called on a program seen complete. With the extension up
// to PAR_MAX new programs are in the driver at once (`flight`, shared by
// every job); without it three calls every program ready at once and the
// queue degrades to one new program per task, its first use the link wait
// (the old slicing). A program still linking after LINK_CAP_MS is left to
// its first draw (`gone`): no job waits on it longer, none blocks on it.
//
// ONE SCHEDULER, one task at a time (setTimeout 0; the 16 ms poll while the
// driver is full or the oldest program still links):
//  1. the driver's room: PAR_MAX − in flight with the extension, else 1
//     (≤ 1 COMPLETION_STATUS query of a busy program: `flight` is trimmed
//     from the front and stops at the first still linking);
//  2. THE FIRST-USE LANE, the part of a class above the top job's: every
//     program a job made, by class then birth (the first view's before a
//     walk's), oldest first; never on one still linking (it ends the lane
//     for this task), a freed one or one past LINK_CAP_MS is skipped;
//  3. the TOP JOB (the highest class with work, then the oldest): its scan
//     at its first task, then ≤ compile_ms of compile() calls, ≤ the room of
//     NEW programs; or texture uploads under the frame's upload_kb;
//  4. the rest of the lane.
// MAIN-THREAD BOUND PER TASK: ≤ compile_ms of calls plus the one that crossed
// it (a compile: three's shader source build, the link is the driver's with
// the extension; a first use; an upload), ≤ USE_MAX first uses, the scan of
// a starting job.
// UPLOAD BUDGET (R8d, the frame budget): a texture upload (initTexture) is
// charged its bytes (texBytes: the mip chain's data, else w x h x depth x 4,
// x 4/3 when three builds the mips) to the current FRAME — from one drawn()
// to the next, or POLL_MS of clock when nothing draws (the Map). A frame
// takes uploads while its bytes are under upload_kb (its first upload always:
// a texture bigger than the budget goes alone); past it the job waits for
// the next frame (the poll, not a spin). Without the extension: one new program per task, a first
// use the link wait.
//
// SLICED COMPILE (client-load-unresponsive): three's compile() builds every
// new program in ONE task, and on an iGPU that task held the main thread for
// 9–18 s before entry (load reports 29675 / 29712: prewarm.compile 9.0 s,
// warm.water 11.6–17.8 s) — the Map froze and every fetch completion (a 2 KB
// livery.json: 13–16 s) waited behind it. A job compiles root's new programs
// one object at a time. A non-leaf is walked too, unless a light sits under
// it (compile(o, …, scene) would count that light twice). A canvas job
// without view.only ends with a whole compile(root), only while something is
// still uncompiled — the same coverage as one compile(); it was
// unconditional, and with the non-leaves left to it, it built a batch in one
// go (a 9.7 s task, 29753).
// OPTIC pass: the target `rt` is bound around each task's compiles (three
// keys a program on it), `cam` is its camera. A material already has its
// canvas program there, so the test is "not yet compiled for this pass +
// object kind" (passTried), and there is no closing whole compile (it would
// be one unsliced task again: warm.compile 2–3.6 s after the Beam, the optic
// copies in one go).
// FIRST USE (three's synchronous link diagnostics + uniform / attribute
// location queries): never inside a frame, never on a program not complete
// (that is the synchronous link wait): up to USE_MAX per task while they are
// cheap (≤ compile_ms). Without the extension every program is "complete" and
// each first use waits for its own link (8 of them were 1.9–2.4 s tasks,
// 29742 — hence one per task once they are not cheap).
// THE READY RULE (r8-m5d, W8: the driver said COMPLETION_STATUS true and the
// first getter still waited 82–527 ms, 180–330 ms after the program's birth):
// with the extension a first use needs the program seen complete in an
// EARLIER task, READY_FLOOR_MS since its birth, and no compile / upload issued
// before it in the same task (a getter is a sync round trip behind them).
// SHARED PROGRAMS: a job whose compile picked up a program another job made
// that is not first-used yet resolves only once that one is (a GLB tile
// wearing the facade keeper's program drew it mid-link: 377 ms).

export const PAR_MAX = 4;
export const LINK_CAP_MS = 30000;
// the gates' frame (no main.js): the R8b numbers — compile 8 ms a task, one
// upload a frame (upload_kb 0: the first upload of a frame always goes)
export const FRAME_GATE = { compile_ms: 8, upload_kb: 0 };
export const USE_MAX = 8;
export const READY_FLOOR_MS = 750;
export const POLL_MS = 16;
// the big textures uploaded one per task before entry (preTex): ≥ this
export const PRETEX_MIN_PX = 512 * 512;
// the classes, highest first: the first view behind the overlay (fvTick) >
// the water variants (warmWater) > a GLB / ways root about to join (the drip
// feeds' prepare) > the pre-entry walk (preWarm, its textures) > the warm-up
// behind the overlay (warmShaders) > the late pass, the optic object
export const PRIO = { first: 0, water: 1, prepare: 2, pre: 3, behind: 4, late: 5 };
const IN_FRAME_KEEP = 8;              // inFrame.first: the first draws that built a program

// R8d5 THE DEVICE ASKED ONCE: three asks the driver LAZILY — on a session's
// first texture upload (extensions.has('EXT_texture_filter_anisotropic'):
// 53 ms inside the near window's first upload, quiet window 2), its first
// anisotropic mipmapped texture (capabilities.getMaxAnisotropy: 491 ms) and
// its first program (KHR_parallel_shader_compile). main.js calls this in the
// renderer-creation task, so no later task pays them. Only names three asks
// in every session anyway, each answer cached by three: the same extensions
// enabled, the same anisotropy clamp — only the task moves (check-compileq 11).
export function askDevice(renderer) {
  renderer.capabilities.getMaxAnisotropy();
  renderer.extensions.has('KHR_parallel_shader_compile');
}

const never = () => false;

export function createCompileQueue({ renderer, camera, act = (l, fn) => (fn ? fn() : undefined),
  now = () => performance.now(), setTimeout: later = (fn, ms) => setTimeout(fn, ms), frame = () => FRAME_GATE,
  taskRoom = () => Infinity }) {
  let tried = new WeakSet();            // materials a job handed to compile()
  let triedMv = 0;                      // ... for this program variant (quest-multiview: renderer.multiviewViews)
  const passTried = new WeakMap();      // material -> Set of `${pid}|${objKey}` an optic job compiled
  const born = new WeakMap();           // program -> when a job made it
  const flight = [];                    // programs handed to the driver, not yet seen complete, oldest first
  const lane = [];                      // { p, job } made, not yet first-used: by class, then birth
  const laneP = new Set();              // the lane's programs
  const seen = new WeakMap();           // program -> the task it was first seen complete in
  const jobs = [];                      // queued / running: by class, then age
  const pins = new Set();
  const st = { classes: {}, made: 0, used: 0, gone: 0, uploads: 0, upBytes: 0, upWaits: 0, inFrame: { n: 0, frames: 0, first: [] } };
  const fb = { t: -Infinity, bytes: 0 };      // the current frame's uploads (UPLOAD BUDGET)
  let seq = 0, timer = false, par = null, tn = 0, issued = false;

  const parallel = () => {
    if (par === null) par = !!renderer.extensions.has('KHR_parallel_shader_compile');
    return par;
  };
  // a program a job may first-use / stop waiting for: freed, complete, or
  // past LINK_CAP_MS (`gone`: freed or past the cap — never first-used)
  const linkState = (p, live) => {
    if (!live.includes(p) || now() - (born.get(p) ?? 0) >= LINK_CAP_MS) return 'gone';
    if (!p.isReady()) return 'linking';
    if (!seen.has(p)) seen.set(p, tn);
    return 'ready';
  };
  // THE READY RULE: a first use only on a program complete since an earlier
  // task, READY_FLOOR_MS old, in a task that issued nothing before it
  const useState = (p, live) => {
    const s = linkState(p, live);
    if (s !== 'ready' || !parallel()) return s;
    return seen.get(p) < tn && now() - (born.get(p) ?? 0) >= READY_FLOOR_MS && !issued ? 'ready' : 'linking';
  };
  // flight trimmed from the front; returns how many are still in the driver
  const inFlight = () => {
    const live = renderer.info.programs;
    while (flight.length && linkState(flight[0], live) !== 'linking') flight.shift();
    return flight.length;
  };

  const drawable = (o) => (o.isMesh || o.isPoints || o.isLine || o.isSprite) && !!o.material;
  const mats = (o) => (Array.isArray(o.material) ? o.material : [o.material]);
  // a material of o has no program yet (`untried`: and no job handed it to
  // compile() — the landing scan's test: the draw is the fallback for those)
  // quest-multiview (the vendored three patch, xrmv.js): a material whose program is the other
  // variant (per-eye vs multiview) than the one the XR frame will draw is not compiled; a switch of
  // the wanted variant starts `tried` afresh
  function uncompiled(o, untried = false) {
    if (!drawable(o)) return false;
    const mv = renderer.multiviewViews ? renderer.multiviewViews(renderer.getRenderTarget(), true) : 0;
    if (mv !== triedMv) { tried = new WeakSet(); triedMv = mv; }
    for (const m of mats(o)) {
      const p = renderer.properties.get(m);
      if ((!p.currentProgram || (p.numMultiviewViews || 0) !== mv) && !(untried && tried.has(m))) return true;
    }
    return false;
  }
  // what of an object (besides its material) picks a program variant:
  // instancing / batching / skinning, the geometry's attributes (vertex alpha,
  // uv1, tangents) and morph targets, shadow receipt
  function objKey(o) {
    const g = o.geometry, at = g && g.attributes;
    let k = o.type + (o.instanceColor ? '+ic' : '') + (o.receiveShadow ? '+rs' : '');
    if (at) for (const n in at) k += ',' + n + (n === 'color' ? at[n].itemSize : '');
    if (g && g.morphAttributes) for (const n in g.morphAttributes) k += ',m' + n;
    return k;
  }
  function passKey(o, pid) {
    if (!drawable(o)) return null;
    const k = pid + '|' + objKey(o);
    for (const m of mats(o)) { const s = passTried.get(m); if (!s || !s.has(k)) return k; }
    return null;
  }
  function compileTry(o, cam, target, pid = null) {
    const k = pid ? passKey(o, pid) : null;
    const ms = renderer.compile(o, cam, target);
    issued = true;
    for (const m of ms) {
      tried.add(m);
      if (!k) continue;
      let s = passTried.get(m);
      if (!s) passTried.set(m, (s = new Set()));
      s.add(k);
    }
    return ms;
  }
  // THE FIRST VIEW at (x, z): an object the view there draws — visible all the
  // way up and its bounding sphere within camera.far (no sphere yet: counted
  // in, at 0 m). Returns its distance (m, the sort key), or Infinity: not in it.
  function firstKey(o, x, z) {
    for (let p = o; p; p = p.parent) if (!p.visible) return Infinity;
    const s = (o.isInstancedMesh || o.isBatchedMesh) ? o.boundingSphere : o.geometry && o.geometry.boundingSphere;
    if (!s) return 0;
    o.updateWorldMatrix(true, false);
    const e = o.matrixWorld.elements, c = s.center;
    const wx = e[0] * c.x + e[4] * c.y + e[8] * c.z + e[12], wz = e[2] * c.x + e[6] * c.y + e[10] * c.z + e[14];
    const d = Math.max(0, Math.hypot(wx - x, wz - z) - s.radius * o.matrixWorld.getMaxScaleOnAxis());
    return d <= camera.far ? d : Infinity;
  }
  // the uncompiled objects of root's first view at (x, z) (fvTick's scan)
  function left(root, x, z) {
    let n = 0;
    root.traverse((o) => { if (uncompiled(o) && firstKey(o, x, z) !== Infinity) n++; });
    return n;
  }

  // the big textures (≥ PRETEX_MIN_PX) with data that is not on the GPU yet
  function texPending(t) {
    const im = t && t.isTexture && !t.isRenderTargetTexture && t.version > 0 ? t.image : null;
    return !!im && (im.width || 0) * (im.height || 0) >= PRETEX_MIN_PX
      && renderer.properties.get(t).__version !== t.version;
  }
  function texList(root, all) {
    const todo = new Set();
    const add = (v) => { if (texPending(v)) todo.add(v); };
    root[all ? 'traverse' : 'traverseVisible']((o) => {
      if (!o.material) return;
      for (const m of mats(o)) {
        for (const k in m) add(m[k]);
        if (m.uniforms) for (const k in m.uniforms) add(m.uniforms[k] && m.uniforms[k].value);
      }
    });
    return [...todo];
  }

  // ------------------------------------------------------------ the jobs
  const rankOf = (prio) => (typeof prio === 'number' ? prio : PRIO[prio] ?? PRIO.late);
  const cls = (j) => st.classes[j.cls] || (st.classes[j.cls] = { queued: 0, running: 0, done: 0 });
  const passId = (ps) => (ps.rt ? ps.rt.texture.uuid : 'canvas') + '/' + ps.cam.uuid;
  function asks(j, o) {
    const prio = o.prio ?? 'late';
    j.rank = rankOf(prio);
    j.cls = typeof prio === 'number' ? String(prio) : prio;
    j.use = o.use || j.label + '.firstUse';
    j.target = o.target || null;
    j.pass = o.pass || null;
    j.view = o.view || null;
    j.all = !!o.all;
    j.stop = o.stop || never;
  }
  function add(j) {
    j.seq = seq++;
    let i = jobs.length;
    while (i > 0 && jobs[i - 1].rank > j.rank) i--;
    jobs.splice(i, 0, j);
    cls(j).queued++;
    kick();
  }
  function enqueue(kind, root, o) {
    if (!root) return Promise.resolve([]);
    const pid = o.pass ? passId(o.pass) : null;
    const label = o.label || (kind === 'tex' ? 'warm.tex' : 'warm.compile');
    const j = jobs.find((x) => x.kind === kind && x.root === root && x.pid === pid && x.label === label);
    if (j && !j.todo) {
      // queued: it scans when it starts, with the newer asks (the higher class)
      const r = j.rank, c = j.cls;
      cls(j).queued--;
      asks(j, o);
      if (r < j.rank) { j.rank = r; j.cls = c; }
      cls(j).queued++;
      jobs.splice(jobs.indexOf(j), 1);
      let i = jobs.length;
      while (i > 0 && (jobs[i - 1].rank > j.rank || (jobs[i - 1].rank === j.rank && jobs[i - 1].seq > j.seq))) i--;
      jobs.splice(i, 0, j);
      return j.p;
    }
    if (j) {
      // running: ONE re-scan after it, with the latest asks
      if (!j.again) { let res; const p = new Promise((r) => { res = r; }); j.again = { o, p, res }; } else j.again.o = o;
      return j.again.p;
    }
    let res;
    const p = new Promise((r) => { res = r; });
    const n = { kind, root, label, pid, p, res, todo: null, open: true, fresh: [], pending: 0, again: null, waitP: new Set() };
    asks(n, o);
    add(n);
    return p;
  }
  const want = (root, o = {}) => enqueue('compile', root, o);
  const upload = (root, o = {}) => enqueue('tex', root, o);
  const steps = (pieces, o = {}) => enqueue('tex', pieces, o);

  // a job's first task: its list (compile: what needs a program, the first
  // view first, nearest first; tex: the pending big textures)
  function start(j) {
    cls(j).queued--; cls(j).running++;
    j.todo = [];                        // started (a scan that throws leaves it empty)
    if (j.kind === 'tex') { j.todo = Array.isArray(j.root) ? j.root.slice() : texList(j.root, j.all); return; }
    const lit = (o) => { let l = false; o.traverse((c) => { if (c.isLight) l = true; }); return l; };
    let todo = [];
    j.root.traverse((o) => { if (need(j, o) && (!o.children.length || !lit(o))) todo.push(o); });
    if (j.view) {
      const key = new Map(todo.map((o) => [o, firstKey(o, j.view.x, j.view.z)]));
      if (j.view.only) todo = todo.filter((o) => key.get(o) !== Infinity);
      todo.sort((a, b) => key.get(a) - key.get(b));
    }
    j.todo = todo;
  }
  const need = (j, o) => (j.pass ? passKey(o, j.pid) !== null : uncompiled(o));
  const under = (o, root) => { for (let p = o; p; p = p.parent) if (p === root) return true; return false; };
  function close(j) {
    j.open = false;
    if (j.todo) j.todo.length = 0;
  }
  function finish(j) {
    jobs.splice(jobs.indexOf(j), 1);
    const c = cls(j);
    if (j.todo) c.running--; else c.queued--;
    c.done++;
    j.res(j.fresh);
    if (j.again) {
      const a = j.again;
      const n = { kind: j.kind, root: j.root, label: j.label, pid: j.pid, p: a.p, res: a.res, todo: null, open: true, fresh: [], pending: 0, again: null, waitP: new Set() };
      asks(n, a.o);
      add(n);
    }
  }
  function kick() {
    if (timer) return;
    timer = true;
    later(step, 0);
  }

  // the task's budget: its first piece always runs, the next ones while
  // under compile_ms since it began AND under the room the task had when it
  // began (taskRoom = sched.js xrRoom: in a headset the ms left before the next
  // XR frame callback's guard, Infinity otherwise — a task never runs into the frame)
  let t0 = 0, spent = false, texWait = false, roomAt = Infinity;
  const can = () => !spent || (now() - t0 < frame().compile_ms && now() - t0 < roomAt);
  function step() {
    timer = false;
    t0 = now();
    roomAt = taskRoom();
    tn++;
    spent = false;
    texWait = false;
    issued = false;
    let usedN = 0, held = false;
    const room = parallel() ? PAR_MAX - act('warm.link', inFlight) : 1;
    let top = null;
    for (const j of jobs) {
      if (!j.open) continue;
      if (j.stop()) { close(j); continue; }
      top = j;
      break;
    }
    const uses = (limit) => {
      const live = renderer.info.programs;
      while (lane.length && !held && usedN < USE_MAX && lane[0].job.rank <= limit && can()) {
        const e = lane[0];
        let s;
        try {
          s = act(e.job.use, () => {
            const s2 = useState(e.p, live);
            if (s2 === 'ready') e.p.getUniforms();
            return s2;
          });
        } catch (err) { console.warn('[compileq] first use:', err); s = 'gone'; }
        if (s === 'linking') { held = true; break; }
        lane.shift();
        laneP.delete(e.p);
        e.job.pending--;
        if (s === 'ready') { st.used++; usedN++; spent = true; } else st.gone++;
      }
    };
    uses(top ? top.rank - 1 : Infinity);
    if (top && can() && (top.kind === 'tex' || room > 0)) {
      // a job that throws ends (what it made is still first-used): one broken
      // root never stalls the queue
      try {
        if (!top.todo) act(top.label, () => start(top));
        if (top.kind === 'tex') texPiece(top); else compilePiece(top, room);
      } catch (err) { console.warn(`[compileq] ${top.label}:`, err); close(top); }
    }
    uses(Infinity);
    for (const j of jobs.slice()) if (!j.open && !j.pending && ![...j.waitP].some((p) => laneP.has(p))) finish(j);
    if (!jobs.length) return;          // idle until the next want
    // at once while there is work the driver has room for, or a lane not
    // waiting on a link; else THE POLL
    const next = jobs.find((j) => j.open);
    const fast = (next && ((next.kind === 'tex' && !texWait) || (next.kind !== 'tex' && room > 0))) || (lane.length && !held);
    timer = true;
    later(step, fast ? 0 : POLL_MS);
  }
  // ≤ compile_ms of compile() calls (+ the one that crossed it), ≤ room NEW programs
  function compilePiece(j, room) {
    const before = new Set(renderer.info.programs);
    const n0 = before.size;
    const pass = j.pass, cam = pass ? pass.cam : camera;
    const closing = !pass && !(j.view && j.view.only);
    act(j.label, () => {
      const old = pass ? renderer.getRenderTarget() : null;
      const face = pass ? renderer.getActiveCubeFace() : 0, mip = pass ? renderer.getActiveMipmapLevel() : 0;
      if (pass) renderer.setRenderTarget(pass.rt);
      try {
        while (j.todo.length && renderer.info.programs.length - n0 < room && can()) {
          const o = j.todo.shift();
          // still in the graph (a disposed one would leak its new program)
          if (under(o, j.root) && need(j, o)) { waitOn(j, compileTry(o, cam, j.target || j.root, j.pid)); spent = true; }
        }
        if (!j.todo.length && (!closing || can())) {
          if (closing && !j.stop()) {
            let rest = false;
            j.root.traverse((o) => { if (!rest && uncompiled(o)) rest = true; });
            if (rest) { waitOn(j, compileTry(j.root, camera, j.target)); spent = true; }
          }
          j.open = false;
        }
      } finally { if (pass) renderer.setRenderTarget(old, face, mip); }
    });
    const made = renderer.info.programs.filter((p) => !before.has(p));
    if (!made.length) return;
    const t = now();
    let i = lane.length;
    while (i > 0 && lane[i - 1].job.rank > j.rank) i--;
    lane.splice(i, 0, ...made.map((p) => ({ p, job: j })));
    for (const p of made) { born.set(p, t); flight.push(p); laneP.add(p); }
    j.fresh.push(...made);
    j.pending += made.length;
    st.made += made.length;
  }
  // SHARED PROGRAMS: the programs of materials j compiled that an older job
  // made and the lane has not first-used yet (j's own new ones join the lane
  // after this) — j resolves only once they are
  function waitOn(j, ms) {
    for (const m of ms) {
      const p = renderer.properties.get(m).currentProgram;
      if (p && laneP.has(p)) j.waitP.add(p);
    }
  }
  // a texture's upload bytes (UPLOAD BUDGET)
  function texBytes(t) {
    const im = t.image || {}, mm = t.mipmaps;
    if (mm && mm.length && mm[0] && mm[0].data) return mm.reduce((n, m) => n + ((m && m.data && m.data.byteLength) || 0), 0);
    const base = im.data && im.data.byteLength ? im.data.byteLength : (im.width || 0) * (im.height || 0) * (im.depth || 1) * 4;
    return t.generateMipmaps ? Math.round(base * 4 / 3) : base;
  }
  // uploads while the frame's bytes are under upload_kb and the task under
  // compile_ms (the ones already on the GPU are passed over); the frame full:
  // the job waits for the next (texWait: the poll)
  function texPiece(j) {
    if (now() - fb.t >= POLL_MS) { fb.t = now(); fb.bytes = 0; }
    while (j.todo.length && can()) {
      if (fb.bytes > 0 && fb.bytes >= frame().upload_kb * 1024) { texWait = true; st.upWaits++; break; }
      const t = j.todo[0], piece = typeof t.run === 'function';
      if (!piece && !texPending(t)) { j.todo.shift(); continue; }
      const b = piece ? t.bytes : texBytes(t);
      act(j.label, piece ? t.run : () => renderer.initTexture(t));
      issued = true;
      j.todo.shift();
      st.uploads++;
      st.upBytes += b;
      fb.bytes += b;
      spent = true;
    }
    if (!j.todo.length) j.open = false;
  }

  // THE IN-FRAME COUNTER: the programs three built inside a draw (the info
  // list's growth across it) — 0 is the goal; the first few draws that built
  // one keep their label, count and frame
  function drawn(fn, label = 'frame') {
    const n0 = renderer.info.programs.length;
    try { return fn(); } finally {
      fb.t = now(); fb.bytes = 0;            // a new frame's upload budget
      const n = renderer.info.programs.length - n0;
      if (n > 0) {
        const f = st.inFrame;
        f.n += n;
        f.frames++;
        if (f.first.length < IN_FRAME_KEEP) f.first.push({ label, n, frame: renderer.info.render ? renderer.info.render.frame : null, at: Math.round(now()) });
      }
    }
  }
  function pin(obj) {
    if (obj) pins.add(obj);
    return obj;
  }
  const busy = (c) => jobs.some((j) => c === undefined || j.cls === c);
  function state() {
    const classes = {};
    for (const k in st.classes) classes[k] = { ...st.classes[k] };
    const f = st.inFrame;
    return { classes, inFlight: flight.length, lane: lane.length, made: st.made, used: st.used, gone: st.gone,
      uploads: st.uploads, upload_bytes: st.upBytes, upload_waits: st.upWaits, pinned: pins.size, inFrame: { n: f.n, frames: f.frames, first: f.first.map((x) => ({ ...x })) } };
  }
  return { want, upload, steps, pin, busy, uncompiled, left, drawn, state };
}
